import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { BatchNumberField } from '../components/batch-number-field';
import { ExpiryDateField } from '../components/expiry-date-field';
import { ItemNotFoundAlert } from '../components/item-not-found-alert';
import { ItemStatusSelector } from '../components/item-status-selector';
import { ItemValidationHeader } from '../components/item-validation-header';
import { PhotoUpload } from '../components/photo-upload/photo-upload';
import { WizardNavigation } from '../components/wizard-navigation';
import { useDeliveryNoteDetail } from '../hooks/use-delivery-note-detail';
import { useItemValidationForm } from '../hooks/use-item-validation-form';
import { WizardLayout } from './wizard-layout';

export function ItemValidationStepPage() {
  const { t } = useTranslation();
  const { itemId } = useParams<{ itemId: string }>();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId') ?? '';
  const { data: deliveryNote } = useDeliveryNoteDetail(draftId);

  const item = deliveryNote?.items.find((i) => i.id === itemId);

  const {
    formState,
    updateField,
    isTraceabilityComplete,
    requiresReason,
    canSubmit,
    isSubmitting,
    handleSubmit,
    navigateToItemsList,
  } = useItemValidationForm(itemId, item);

  if (!item) {
    return <ItemNotFoundAlert />;
  }

  return (
    <WizardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 10 }}>
        <ItemValidationHeader productName={item.productName} />

        <BatchNumberField
          value={formState.batchNumber}
          onChange={(value) => updateField('batchNumber', value)}
          showError={!isTraceabilityComplete}
        />

        <ExpiryDateField
          value={formState.expiryDate}
          onChange={(value) => updateField('expiryDate', value)}
          showError={!isTraceabilityComplete}
        />

        <PhotoUpload
          value={formState.labelPhotos}
          onChange={(photos) => updateField('labelPhotos', photos)}
          maxFiles={5}
          label={t('goodsReceiving.photoLabelOptional')}
        />

        <ItemStatusSelector
          status={formState.status}
          onStatusChange={(status) => updateField('status', status)}
          reason={formState.statusReason}
          onReasonChange={(reason) => updateField('statusReason', reason)}
          showReasonError={requiresReason}
        />
      </Box>

      <WizardNavigation
        canGoBack={true}
        canGoNext={canSubmit}
        onBack={navigateToItemsList}
        onNext={handleSubmit}
        nextLabel={isSubmitting ? t('goodsReceiving.itemsSaving') : t('goodsReceiving.itemsSave')}
        isLoading={isSubmitting}
      />
    </WizardLayout>
  );
}
